/**
 * Máscaras de digitação dos campos de texto (#288)
 *
 * Usadas no `onChange` dos campos para que cada um aceite só o tipo de dado certo:
 * telefone e documentos só com dígitos (letras somem na hora), valores em reais com um
 * único separador decimal. A máscara de CEP fica em `utils/cep.ts`.
 *
 * @author Equipe VendeAi
 * @since 1.6.0
 */

/** Só os dígitos do texto */
export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Máscara de telefone brasileiro com DDD: (11) 98765-4321 ou (11) 3333-4444.
 * Letras e símbolos são descartados; no máximo 11 dígitos. Um número colado com o 55
 * do Brasil (12 ou 13 dígitos) perde o 55, como em `formatPhone`.
 */
export function maskPhone(value: string, previous?: string): string {
  let d = onlyDigits(value);
  // Digitou um número a mais num telefone já completo (ex.: DDD 55, do RS): mantém o
  // anterior em vez de tirar o "55" e embaralhar; o 55 sai só ao colar ou autopreencher
  if (previous !== undefined && onlyDigits(previous).length === 11 && d.length === 12 && d.startsWith(onlyDigits(previous))) {
    return maskPhone(previous);
  }
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  d = d.slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Telefone com DDI (ex.: WhatsApp "55 11 98765-4321"): aceita dígitos, espaço,
 * parênteses, hífen e "+" no início; letras são descartadas. No máximo 13 dígitos.
 */
export function maskPhoneWithDdi(value: string): string {
  let out = '';
  let digits = 0;
  for (const char of value) {
    if (/\d/.test(char)) {
      if (digits >= 13) continue;
      digits += 1;
      out += char;
    } else if (char === '+' ? out.trim() === '' : /[\s()-]/.test(char)) {
      out += char;
    }
  }
  return out;
}

/** Mensagem padrão de telefone incompleto */
export const PHONE_INVALID_MESSAGE = 'Telefone inválido: informe DDD + número (10 ou 11 dígitos)';

/** Mensagem padrão de nome vazio ou só com espaços */
export const NAME_REQUIRED_MESSAGE = 'Informe o nome (não pode ficar em branco)';

/** E-mail digitado: sem espaços e em minúsculas */
export function normalizeEmail(value: string): string {
  return value.replace(/\s/g, '').toLowerCase();
}

/** O telefone tem DDD + número (10 ou 11 dígitos), com ou sem o 55 do Brasil? */
export function isPhoneComplete(value: string): boolean {
  let d = onlyDigits(value);
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  return d.length === 10 || d.length === 11;
}

/** Máscara de CPF: 000.000.000-00 */
export function maskCPF(value: string): string {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** Máscara de CNPJ: 00.000.000/0000-00 */
export function maskCNPJ(value: string): string {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Máscara de CPF ou CNPJ, conforme a quantidade de dígitos (até 11 = CPF) */
export function maskDocument(value: string): string {
  return onlyDigits(value).length <= 11 ? maskCPF(value) : maskCNPJ(value);
}

/**
 * Valor em reais ou quantidade decimal digitado num campo de texto (ex.: "1.299,90"):
 * mantém só dígitos, ponto e vírgula. Letras e outros símbolos são descartados; a
 * conferência do valor fica no salvar (`parseDecimalInput`).
 */
export function maskDecimal(value: string): string {
  return value.replace(/[^\d.,]/g, '');
}

/**
 * Converte o texto de um campo decimal em número: "12,50" → 12.5, "1.299,90" → 1299.9,
 * "99.90" → 99.9. Devolve `null` quando vazio e `NaN` quando não é um número.
 */
export function parseDecimalInput(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  const text = value.trim().replace(/[R$\s]/g, '');
  if (text === '') return null;
  if (!/^[\d.,]*\d[\d.,]*$/.test(text)) return NaN;
  const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
  return /^\d*\.?\d*$/.test(normalized) ? Number(normalized) : NaN;
}
